import { useState } from "react"
import { useQuery } from "@tanstack/react-query"

/**
 * Postfach-Auswahl im Posteingang (Berggötz Holzbau, 29.09.2026).
 *
 * Ein Mandant kann mehrere Postfächer haben. Im Posteingang wählt man
 * „Alle Postfächer" (alles zusammen, jede Mail mit Kennung) oder genau eines —
 * dann kommen Ordnerliste, Mails, Ordner-Aktionen und der Absender neuer Mails
 * aus diesem Postfach. Werte: "" = alle, "haupt" = Hauptpostfach, sonst die
 * Adresse. Die Wahl wird je Mandant gemerkt.
 */
export type PostfachListe = {
  haupt: { adresse: string; bezeichnung: string }
  weitere: { id: number; adresse: string; bezeichnung: string; aktiv: boolean }[]
}

export const HAUPT = "haupt"
const SPEICHER = "emails-postfach"

export function usePostfaecher(tenant: string) {
  return useQuery<PostfachListe>({
    queryKey: ["postfaecher", tenant],
    queryFn: async () => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(tenant)}/postfaecher`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: !!tenant,
    staleTime: 1000 * 60 * 5,
  })
}

function gemerkt(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(SPEICHER) || "{}") || {} } catch { return {} }
}

export function usePostfachWahl(tenant: string) {
  const liste = usePostfaecher(tenant).data
  const [wahlen, setWahlen] = useState<Record<string, string>>(gemerkt)
  const weitere = (liste?.weitere || []).filter((p) => p.aktiv)
  const gespeichert = wahlen[tenant] || ""
  // Nur gültige Werte: entferntes Postfach oder Mandant ohne weitere → alle.
  const gueltig = gespeichert === HAUPT || weitere.some((p) => p.adresse === gespeichert) ? gespeichert : ""
  const setWahl = (w: string) => {
    const neu = { ...wahlen, [tenant]: w }
    setWahlen(neu)
    try { localStorage.setItem(SPEICHER, JSON.stringify(neu)) } catch { /* privat/voll */ }
  }
  return { wahl: weitere.length ? gueltig : "", setWahl, weitere, haupt: liste?.haupt }
}

/** Anzeigename eines Postfachs für Auswahllisten. */
export function postfachName(p: { adresse: string; bezeichnung: string }): string {
  return p.bezeichnung && p.bezeichnung !== p.adresse ? `${p.bezeichnung} (${p.adresse})` : p.adresse
}

/** Query-Anhang `&postfach=…` (leer bei „alle"). */
export function postfachQuery(wahl: string | undefined | null): string {
  return wahl ? `&postfach=${encodeURIComponent(wahl)}` : ""
}
